import React, { useEffect, useRef } from "react";
import { industryTicker } from "../data/reportData";

const SPEED = 84; // px per second
const TILT = 0;
const ARC = 0;
const FONT = "clamp(22px, 2.4vw, 38px)";

export default function IndustryTicker() {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    let geom = [];
    let halfWidth = 0;
    let stepWidth = 1;
    let offset = 0;
    let raf;
    let last = performance.now();

    const measure = () => {
      const items = itemRefs.current.filter(Boolean);
      geom = items.map((el) => ({ el, center: el.offsetLeft + el.offsetWidth / 2 }));
      halfWidth = track.scrollWidth / 2;
      stepWidth = halfWidth / industryTicker.length;
    };

    const lerp = (a, b, t) => Math.round(a + (b - a) * t);

    const tick = (now) => {
      const dt = Math.min(64, now - last) / 1000;
      last = now;
      offset -= SPEED * dt;
      if (offset <= -halfWidth) offset += halfWidth;

      const cw = container.clientWidth;
      const mid = cw / 2;
      track.style.transform = `translate3d(${offset}px,0,0)`;

      for (const g of geom) {
        const x = g.center + offset;
        if (x < -stepWidth || x > cw + stepWidth) {
          g.el.style.opacity = "0";
          continue;
        }
        const dNorm = (x - mid) / (cw / 2); // -1 .. 1
        const steps = Math.abs(x - mid) / stepWidth;
        const blur = Math.min(8, Math.pow(steps, 1.1) * 3);
        const opacity = Math.max(0.3, 1 - 0.26 * steps);
        const t = Math.max(0, 1 - steps); // 1 at dead centre
        const c = lerp(148, 17, t);
        g.el.style.opacity = String(opacity);
        g.el.style.filter = blur < 0.25 ? "none" : `blur(${blur.toFixed(2)}px)`;
        g.el.style.color = `rgb(${c},${c},${c})`;
        g.el.style.transform = `translateY(${(-(dNorm * dNorm) * ARC).toFixed(1)}px) rotate(${(-dNorm * TILT).toFixed(2)}deg)`;
      }
      raf = requestAnimationFrame(tick);
    };

    measure();
    raf = requestAnimationFrame(tick);
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  let idx = 0;
  return (
    <div
      id="industry-ticker-band"
      ref={containerRef}
      className="relative bg-white overflow-hidden pt-[50px] pb-[80px]"
      data-testid="industry-ticker"
    >
      <div ref={trackRef} className="flex w-max items-center will-change-transform">
        {[0, 1].map((copy) => (
          <div key={copy} className="flex items-center shrink-0" aria-hidden={copy === 1}>
            {industryTicker.map((name) => (
              <span
                key={`${copy}-${name}`}
                ref={(el) => (itemRefs.current[idx++] = el)}
                className="mx-7 md:mx-9 whitespace-nowrap font-display font-medium tracking-[-0.01em] will-change-transform"
                style={{ fontSize: FONT, color: "rgb(148,148,148)" }}
              >
                {name}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
